import { type ClassValue, clsx } from 'clsx';
import crypto from 'crypto';
import {
  addDays,
  compareAsc,
  format,
  getDay,
  isSameDay,
  parse,
  set,
} from 'date-fns';
import { getTimezoneOffset, toZonedTime } from 'date-fns-tz';
import { camelCase, startCase } from 'lodash';
import { extendTailwindMerge } from 'tailwind-merge';
import type { OptionType } from '@/components/ui/multi-select';
import { isSystemUserEmail } from '@/lib/auth/system-user';
import type {
  ActivateOpeningMessageThreadData,
  MessageThreadData,
} from '@/lib/message-thread/types';
import type { UAS } from '@/lib/monolith/types-v2/common-schemas/uas';
import { type GoActionPermission, PERMISSIONS } from '@/lib/permission';
import type { MonolithBookedSlot } from './monolith/types-v2/common-schemas/booked-slot';

export function canManageApplicants(user: {
  permissions?: Record<string, string> | null;
  role?: string | null;
  internal?: boolean | null;
  owner?: boolean | null;
}): boolean {
  return hasPermission(user, PERMISSIONS.HIRE_GO_MANAGE_APPLICANTS);
}

export function canManageUsers(user: {
  permissions?: Record<string, string> | null;
  role?: string | null;
  internal?: boolean | null;
  owner?: boolean | null;
}): boolean {
  return hasPermission(user, PERMISSIONS.HIRE_GO_MANAGE_USERS);
}

// Gates editing an opening's details, never reading them: the values render for
// every user, and only the form is withheld.
export function canManageOpenings(user: {
  permissions?: Record<string, string> | null;
  role?: string | null;
  internal?: boolean | null;
  owner?: boolean | null;
}): boolean {
  return hasPermission(user, PERMISSIONS.HIRE_GO_MANAGE_OPENINGS);
}

// Gates editing the account's card field lists, never reading them: every user
// renders their cards from the saved config, so withholding it would blank the
// surface rather than protect anything.
export function canManageCardDisplay(user: {
  permissions?: Record<string, string> | null;
  role?: string | null;
  internal?: boolean | null;
  owner?: boolean | null;
}): boolean {
  return hasPermission(user, PERMISSIONS.HIRE_GO_MANAGE_CARD_DISPLAY);
}

// Monolith derives the view key from the manage key as it reads a stored
// permission set, so a manage grant normally arrives with both. The manage check
// here covers a set written straight to the column without that pass.
export function canViewNotes(user: {
  permissions?: Record<string, string> | null;
  role?: string | null;
  internal?: boolean | null;
  owner?: boolean | null;
}): boolean {
  return (
    hasPermission(user, PERMISSIONS.HIRE_GO_VIEW_NOTES) ||
    hasPermission(user, PERMISSIONS.HIRE_GO_MANAGE_NOTES)
  );
}

export function canManageNotes(user: {
  permissions?: Record<string, string> | null;
  role?: string | null;
  internal?: boolean | null;
  owner?: boolean | null;
}): boolean {
  return hasPermission(user, PERMISSIONS.HIRE_GO_MANAGE_NOTES);
}

// Hire decides who may change the account's Hire Go switches (owner, admin,
// super user) and serves the answer on whoami; Hire Go never restates the rule.
export function canManageAccountSettings(user: {
  canManageAccountSettings?: boolean | null;
}): boolean {
  return user.canManageAccountSettings ?? false;
}

export function canManagePersonalSettings(user: {
  email?: string | null;
}): boolean {
  return !isSystemUserEmail(user.email);
}

export function canManageActivationFlows(user: {
  permissions?: Record<string, string> | null;
  role?: string | null;
  internal?: boolean | null;
  owner?: boolean | null;
}): boolean {
  return hasPermission(user, PERMISSIONS.HIRE_GO_MANAGE_ACTIVATION_FLOWS);
}

// The one rule for the per-action keys. Every caller ANDs it with the rule the
// action already had, so with `hire-go-cue-actions` off nothing changes. Hire
// granted every key to each role and user holding `hire_go`, so turning the flag
// on withholds an action only where an admin has since removed its key. Hire's
// `admin` grant covers every action key whatever the role is named, while
// `hasPermission` recognizes an admin by role name only.
export function canTakeGoAction(
  user: {
    permissions?: Record<string, string> | null;
    role?: string | null;
    owner?: boolean | null;
  },
  permission: GoActionPermission,
  cueActionsEnabled: boolean
): boolean {
  return (
    !cueActionsEnabled ||
    user.permissions?.admin === '1' ||
    hasPermission(user, permission)
  );
}

export function hasPermission(
  user: {
    permissions?: Record<string, string> | null;
    role?: string | null;
    owner?: boolean | null;
  },
  permission: string
): boolean {
  if (user.owner) {
    return true;
  }

  if (isAdmin(user.role)) {
    return true;
  }

  // if permissions are not set, return false
  if (!user.permissions) {
    return false;
  }

  // if user's missing Hire Go product access, return false
  if (!user.permissions[PERMISSIONS.HIRE_GO]) {
    return false;
  }

  // Permissions return "1" when granted, so we check for truthy values
  return !!user.permissions[permission];
}

export function isAdmin(hireRole?: string | null) {
  return (
    hireRole?.toLowerCase() === 'admin' ||
    hireRole?.toLowerCase() === 'administrator'
  );
}

// intentionally not doing currency here
export const formatNumber = (value?: number | string | null) =>
  value
    ? new Intl.NumberFormat('en-US', {
        style: 'decimal',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(typeof value === 'string' ? Number(value) : value)
    : null;

export function snakeToTitleCase(str: string) {
  return startCase(camelCase(str));
}
export const sleep = (ms: number) =>
  new Promise((resolve) => setTimeout(resolve, ms));

// The typography roles styles/globals.css defines. Each `text-ftnx-<role>` class sets
// size, line height, weight, and color; its `-inherit` twin drops the color.
const FTNX_TEXT_ROLES = [
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'large1',
  'large2',
  'medium1',
  'medium2',
  'body1',
  'body2',
  'subtitle1',
  'subtitle2',
  'inactive1',
  'inactive2',
  'link1',
  'link2',
  'small1',
  'small2',
  'caption',
];

// tailwind-merge reads an unknown `text-*` class as a text color, so without this group a
// color utility after a role drops the role.
const twMerge = extendTailwindMerge<'ftnx-text-role'>({
  extend: {
    classGroups: {
      shadow: [{ 'shadow-ftnx': ['sm', 'base', 'lg', 'selected'] }],
      'ftnx-text-role': [
        {
          'text-ftnx': [
            ...FTNX_TEXT_ROLES,
            ...FTNX_TEXT_ROLES.map((role) => `${role}-inherit`),
          ],
        },
      ],
    },
  },
});

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function getSlotsForDate(
  bookedSlots: Array<MonolithBookedSlot>,
  date: Date,
  timeZone: string | undefined
) {
  const calendarDate = timeZone ? toZonedTime(date, timeZone) : date;
  return bookedSlots
    .filter((bookedSlot) =>
      isSameDay(
        timeZone
          ? toZonedTime(bookedSlot.start_time, timeZone)
          : new Date(bookedSlot.start_time),
        calendarDate
      )
    )
    .sort((slotA, slotB) =>
      compareAsc(new Date(slotA.start_time), new Date(slotB.start_time))
    );
}

const DEFAULT_FORMAT_DATE_OPTIONS: Intl.DateTimeFormatOptions = {
  month: 'long',
  day: 'numeric',
  year: 'numeric',
};

export function formatDate(
  input?: string | number | Date,
  opts: Intl.DateTimeFormatOptions = DEFAULT_FORMAT_DATE_OPTIONS,
  locale = 'en-US'
): string {
  if (!input) {
    return '';
  }

  const dateOptions = { ...DEFAULT_FORMAT_DATE_OPTIONS, ...opts };

  const date = new Date(input);
  return date.toLocaleDateString(locale, dateOptions);
}

// Returns date in YYYY-MM-DD format
export function formatDateYYYYMMDD(date: Date): string {
  const year = date.getFullYear();

  // getMonth() returns 0-indexed month, so add 1 for correct month number
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  const formattedTime = `${year}-${month}-${day}`;
  return formattedTime;
}

export type PayType = 'hour' | 'day' | 'month' | 'year';
function getPayTypeText(type: PayType) {
  switch (type) {
    case 'hour':
      return ' / hr';
    case 'day':
      return ' / day';
    case 'month':
      return ' / mo';
    case 'year':
      return ' / yr';
    default:
      return '';
  }
}

type FormatFixedPayRange = {
  variant: 'fixed';
  pay: number | string;
};

type FormatRangedPayRange = {
  variant: 'ranged';
  min?: number | string | null;
  max?: number | string | null;
};

type FormatPayRangeArgs = { payType: PayType; currencyCode?: string } & (
  FormatFixedPayRange | FormatRangedPayRange
);
function isFixedPayRange(
  args: Omit<FormatPayRangeArgs, 'payType' | 'currencyCode'>
): args is FormatFixedPayRange {
  return args.variant === 'fixed';
}

export function formatPayRange({
  payType,
  currencyCode = 'USD',
  ...args
}: FormatPayRangeArgs): string {
  const typeText = payType ? getPayTypeText(payType) : '';

  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currencyCode,
  });

  if (isFixedPayRange(args)) {
    const { pay } = args;

    return pay
      ? `${formatter.format(
          typeof pay === 'string' ? Number(pay) : pay
        )}${typeText}`
      : '';
  }

  const { min, max } = args;

  return min && max
    ? `${formatter.format(
        typeof min === 'string' ? Number(min) : min
      )} - ${formatter.format(
        typeof max === 'string' ? Number(max) : max
      )}${typeText}`
    : '';
}

/**
 * @deprecated Use the new `formatPayRange` function instead which has a discriminated union on variant for range or fixed
 */
export function formatFixedPay(value: number, type: PayType): string {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  });
  const typeText = getPayTypeText(type);
  return `${formatter.format(value)}${typeText}`;
}

export function removeEmpty(obj: Record<string, unknown>) {
  return Object.fromEntries(Object.entries(obj).filter(([, v]) => v != null));
}

export type Entries<T> = {
  [K in keyof T]-?: [K, T[K]];
}[keyof T][];

/**
 * WARNING: `entries` does not guard against excess properties, be sure to guard
 * against this
 */
export function entries<T extends Record<string, unknown>>(obj: T): Entries<T> {
  return Object.entries(obj) as Entries<T>;
}

export const dayOfWeekMap = {
  sun: 0,
  mon: 1,
  tue: 2,
  wed: 3,
  thu: 4,
  fri: 5,
  sat: 6,
};

// generates a list of times in 15 minute intervals for the whole day
// examples: '9:30am', '12:15pm', '1:00pm'
export function generateTimeOptions() {
  const times = [];
  for (let i = 0; i < 24; i++) {
    for (let j = 0; j < 60; j += 15) {
      const hour = i % 12 || 12;
      const minute = j === 0 ? '00' : j;
      const period = i < 12 ? 'am' : 'pm';
      const time = `${hour}:${minute}${period}`;
      const option = {
        label: time,
        value: time,
      };
      times.push(option);
    }
  }
  return times;
}

/**
 * Converts a Date object to the corresponding day key used in UAS schedule
 * @param date - The date to convert
 * @returns The day key ('sun', 'mon', 'tue', etc.)
 */
export function getDayKeyFromDate(date: Date): keyof UAS['schedule'] {
  const dayNames = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const;
  return dayNames[date.getDay()];
}

/**
 * Extracts availability time ranges for a specific date from UAS schedule
 * @param date - The date to get availability for
 * @param uas - The user availability settings
 * @returns Array of time ranges for that day, or default 9am-5pm if none exists
 */
export function getAvailabilityForDate(
  date: Date,
  uas?: UAS
): Array<{ start_time: string; end_time: string }> {
  if (!uas?.schedule) {
    return [{ start_time: '9:00am', end_time: '5:00pm' }];
  }

  const dayKey = getDayKeyFromDate(date);
  const dayAvailability = uas.schedule[dayKey];

  if (!dayAvailability || dayAvailability.length === 0) {
    return [{ start_time: '9:00am', end_time: '5:00pm' }];
  }

  return dayAvailability.map((slot) => ({
    start_time: slot.start_time,
    end_time: slot.end_time,
  }));
}

export function getNextWeekdayDate(
  baseDate: Date,
  targetDayOfWeek: number,
  timeStr: string
): Date {
  // Get the current day of the week
  const currentDayOfWeek = getDay(baseDate);

  // Calculate the difference in days to the next occurrence of the target day
  let daysToAdd = (targetDayOfWeek - currentDayOfWeek + 7) % 7;
  if (daysToAdd === 0 && baseDate.getHours() > parseInt(timeStr, 10)) {
    // If today is the target day but the time has passed, set to next week's same day
    daysToAdd = 7;
  }

  // Add calculated days to the base date
  let resultDate = addDays(baseDate, daysToAdd);

  // Adjust the result date's time based on the input time string
  // @ts-expect-error need to handle regex failures
  const [hourStr, minuteStr] = timeStr.match(/\d+/g);
  const period = timeStr.match(/am|pm/i)?.[0];
  const hour =
    (parseInt(hourStr, 10) % 12) + (period?.toLowerCase() === 'pm' ? 12 : 0);
  resultDate = set(resultDate, {
    hours: hour,
    minutes: parseInt(minuteStr, 10),
    seconds: 0,
  });

  return resultDate;
}

export function formatDateToISO(date: Date): string {
  return format(date, 'yyyy-MM-dd');
}

/**
 * Safely constructs a local Date from a YYYY-MM-DD string by anchoring at noon
 * to avoid timezone-based day shifts when rendering just the date.
 */
export function safeLocalDateFromYMD(dateStr: string): Date {
  return new Date(`${dateStr}T12:00:00`);
}

/**
 * Normalizes a time string like "9:00" or "9" to HH:mm (e.g., "09:00").
 */
export function normalizeHHmm(time: string): string {
  const [hRaw, mRaw] = time.split(':');
  const hours = (hRaw ?? '0').padStart(2, '0');
  const minutes = (mRaw ?? '00').padStart(2, '0');
  return `${hours}:${minutes}`;
}

/**
 * Converts a 12-hour time string like "9:00am" to zero-padded 24-hour HH:mm.
 */
export function convertTo24Hour(time12h: string): string {
  const [time, modifier] = time12h.split(/(am|pm)/i);
  const [hRaw, mRaw] = time.trim().split(':');
  let hours = (hRaw ?? '').trim();
  const minutes = (mRaw ?? '00').trim().padStart(2, '0');

  if (hours === '12') {
    hours = '00';
  }
  if (modifier?.toLowerCase() === 'pm') {
    hours = (parseInt(hours, 10) + 12).toString();
  }
  return `${hours.padStart(2, '0')}:${minutes}`;
}

export function getTimezoneOffsetString(
  timezone: string,
  date: Date = new Date()
): string {
  // Convert the local time to the given timezone time
  const zonedDate = toZonedTime(date, timezone);
  // Get the offset in minutes from the timezone
  const offsetMilliseconds = getTimezoneOffset(timezone, zonedDate);
  // Convert milliseconds to minutes
  const offsetMinutes = offsetMilliseconds / 60000;
  // Calculate hours and minutes from the total offset in minutes
  const sign = offsetMinutes < 0 ? '-' : '+';
  const hours = Math.floor(Math.abs(offsetMinutes) / 60);
  const minutes = Math.abs(offsetMinutes) % 60;

  return `${sign}${hours.toString().padStart(2, '0')}:${minutes
    .toString()
    .padStart(2, '0')}`;
}

/**
 * Constructs a Date representing a local wall-clock time (HH:mm) on a given
 * calendar date (YYYY-MM-DD) in the specified IANA timezone by embedding the
 * proper offset into an ISO string.
 */
export function buildDateInZone(
  ymd: string,
  hhmm: string,
  timeZone: string
): Date {
  const offset = getTimezoneOffsetString(timeZone, safeLocalDateFromYMD(ymd));
  const iso = `${ymd}T${normalizeHHmm(hhmm)}:00${offset}`;
  return new Date(iso);
}

export function convertTimeToISO({
  timeStr = '10:15am',
  dateStr = '2024-04-20',
  timeZoneOffset = '-07:00',
}: {
  timeStr: string;
  dateStr: string;
  timeZoneOffset?: string;
}) {
  // Parse the hour and minute from the time string
  const timeRegex = /(\d+):(\d+)(am|pm)/i;
  const [, hour, minute, period] = timeStr.match(timeRegex) ?? [];

  // Adjust hour for 12-hour time format
  let adjustedHour = parseInt(hour, 10);
  if (period.toLowerCase() === 'pm' && adjustedHour < 12) {
    adjustedHour += 12;
  }
  if (period.toLowerCase() === 'am' && adjustedHour === 12) {
    adjustedHour = 0;
  }

  // Parse the base date with date-fns
  const baseDate = parse(
    `${dateStr} ${adjustedHour}:${minute}`,
    'yyyy-MM-dd H:mm',
    new Date()
  );

  // Format the date as ISO string with timezone offset
  return format(baseDate, `yyyy-MM-dd'T'HH:mm:ss${timeZoneOffset}`);
}

// Function to format a date string (YYYY-MM-DD) into ISO 8601 format with current time
export function formatDateToISOWithCurrentTime(dateString: string) {
  const now = new Date();

  const formattedDate = new Date(dateString);
  formattedDate.setHours(
    now.getHours(),
    now.getMinutes(),
    now.getSeconds(),
    now.getMilliseconds()
  );

  return formattedDate.toISOString();
}

export function generateSignature(
  nonce: string,
  timestamp: string,
  apiKey: string
) {
  // Create the signature base
  const signatureBase = [nonce, timestamp, apiKey].join('\n');

  // Create HMAC using SHA256
  const hmac = crypto.createHmac('sha256', apiKey);

  // Update the HMAC with the signature base
  hmac.update(signatureBase);

  // Calculate the HMAC digest in hexadecimal format
  const signature = hmac.digest('hex');

  return signature;
}

export const generateUrlSafeBase64 = (data: number) => {
  // Convert the number to a string, then to a Buffer
  const buffer = Buffer.from(data.toString(), 'utf-8');

  // Convert the buffer to a Base64 string
  let base64 = buffer.toString('base64');

  // Replace URL unsafe characters
  base64 = base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');

  return base64;
};

/**
 * What a host or reviewer picker reads from an eligible user. A reviewer
 * candidate who never set availability carries no UAS id, so pickers never
 * read one.
 */
export type HostCandidate = Pick<UAS, 'user' | 'schedule'>;

export const hasAvailability = (uas: Pick<UAS, 'schedule'>): boolean =>
  Object.values(uas.schedule).some((slots) => (slots?.length ?? 0) > 0);

export const getHostOptions = (uases: Array<HostCandidate>): OptionType[] => {
  return uases.map((uas) => {
    const notAvailable = !hasAvailability(uas);
    const { name, email, id: userId } = uas.user;
    const label = name?.trim() ? `${name} (${email})` : email;

    return {
      label: notAvailable ? `${label} (no availability)` : label,
      value: notAvailable ? `${userId}-disabled` : userId,
    };
  });
};

/** Reviewer options: a reviewer needs no availability, so none is disabled. */
export const getReviewerOptions = (
  candidates: Array<Pick<UAS, 'user'>>
): OptionType[] =>
  candidates.map(({ user: { email, id } }) => ({ label: email, value: id }));

export function getDefaultHostsForThread<T extends Pick<UAS, 'user'>>(
  messageThreadData: ActivateOpeningMessageThreadData | MessageThreadData,
  eligibleHosts: T[]
): T[] {
  const existingHostMonolithUserIds =
    (messageThreadData.hosts?.length ?? 0) > 0
      ? (messageThreadData.hosts?.map(({ value }) => value) ?? [])
      : [];
  const defaultHosts =
    existingHostMonolithUserIds?.length > 0
      ? eligibleHosts.filter(({ user: { id: monolithUserId } }) =>
          existingHostMonolithUserIds.includes(monolithUserId)
        )
      : [];

  return defaultHosts;
}

/**
 * Adds the acting user's own row to `hosts` when eligible, available, and not
 * already present. Additive only — an existing selection is never removed or
 * reordered. `UAS.user.id` and `monolithUserId` share one identifier space,
 * so this is an exact match, not a heuristic.
 *
 * Skips a host with no availability: its `getHostOptions` value is not a uuid.
 */
export function withCurrentUserAsDefaultHost<T extends HostCandidate>(
  hosts: T[],
  eligibleHosts: T[],
  currentUserMonolithId: string
): T[] {
  if (hosts.some(({ user: { id } }) => id === currentUserMonolithId)) {
    return hosts;
  }

  const currentUserHost = eligibleHosts.find(
    ({ user: { id } }) => id === currentUserMonolithId
  );

  return currentUserHost && hasAvailability(currentUserHost)
    ? [...hosts, currentUserHost]
    : hosts;
}

/**
 * Adds the acting user's own row to `reviewers` when eligible and not already
 * present, with or without availability. Additive, as
 * `withCurrentUserAsDefaultHost` is.
 */
export function withCurrentUserAsDefaultReviewer<T extends Pick<UAS, 'user'>>(
  reviewers: T[],
  eligibleReviewers: T[],
  currentUserMonolithId: string
): T[] {
  if (reviewers.some(({ user: { id } }) => id === currentUserMonolithId)) {
    return reviewers;
  }

  const currentUserReviewer = eligibleReviewers.find(
    ({ user: { id } }) => id === currentUserMonolithId
  );

  return currentUserReviewer ? [...reviewers, currentUserReviewer] : reviewers;
}

// biome-ignore lint/suspicious/noExplicitAny: noop accepts and returns any type by design
export const noop = (..._: Array<any>): any | Promise<any> => {
  return;
};
